import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
    addWorkItem,
    updateWorkItem,
    removeWorkItem,
    addJobNote,
    fetchJob,
} from '../store/slices/jobSlice';

/**
 * The five things an open job detail screen can do to a job.
 *
 * Both detail screens — the desktop JobDetailModal (via JobsPage) and the phone's
 * MobileJobDetail — need exactly these, and they are pure dispatch plumbing: no
 * screen-specific behaviour, nothing that should differ between a 27" monitor and a
 * phone held under a bonnet. They were written once in JobsPage; the quotes screen
 * needed them second, so they live here rather than becoming a second copy that
 * drifts (the quote DTOs on the backend drifted within a day of being copied).
 *
 * ⚠️ Two return shapes, deliberately, and they are not interchangeable:
 *
 *   - addNote / refreshJob are AWAITED by their callers. The notes composer needs the
 *     saved note back to append it to the feed, so a rejection has to throw rather
 *     than resolve — a caller that appends `undefined` renders a blank note that
 *     disappears on the next refresh.
 *   - addWorkItem / editWorkItem / removeWorkItem are fire-and-forget: the slice
 *     re-reads the job, and the screens read `operationLoading` for progress.
 *
 * @param job the open job, or null when nothing is open. Every action no-ops on null
 *            so a screen never has to guard each call site.
 */
export const useJobDetailActions = (job) => {
    const dispatch = useDispatch();
    const jobUuid = job?.uuid ?? null;

    const addNote = useCallback(async ({ content, noteType, customerVisible, workItemUuid }) => {
        if (!jobUuid) return null;
        const result = await dispatch(addJobNote({
            jobUuid,
            data: {
                content,
                noteType: noteType || 'PROGRESS_UPDATE',
                customerVisible: !!customerVisible,
                workItemUuid: workItemUuid || null,
            },
        }));
        if (addJobNote.fulfilled.match(result)) return result.payload;
        // The slice puts the server's own sentence in the payload on rejection —
        // which names what is wrong — so it is thrown rather than a generic message.
        throw new Error(result.payload || 'Failed to add note');
    }, [dispatch, jobUuid]);

    const refreshJob = useCallback(async () => {
        if (!jobUuid) return null;
        const result = await dispatch(fetchJob(jobUuid));
        return fetchJob.fulfilled.match(result) ? result.payload : null;
    }, [dispatch, jobUuid]);

    const add = useCallback((payload) => {
        if (jobUuid) dispatch(addWorkItem({ jobUuid, data: payload }));
    }, [dispatch, jobUuid]);

    const edit = useCallback((payload, workItemUuid) => {
        if (jobUuid && workItemUuid) dispatch(updateWorkItem({ jobUuid, workItemUuid, data: payload }));
    }, [dispatch, jobUuid]);

    const remove = useCallback((workItemUuid) => {
        if (jobUuid && workItemUuid) dispatch(removeWorkItem({ jobUuid, workItemUuid }));
    }, [dispatch, jobUuid]);

    return useMemo(() => ({
        addWorkItem: add,
        editWorkItem: edit,
        removeWorkItem: remove,
        addNote,
        refreshJob,
    }), [add, edit, remove, addNote, refreshJob]);
};

export default useJobDetailActions;
